import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import TopNav from "../components/TopNav.jsx";

/**
 * Tiny Homes · county-level proposals · a nail in itself.
 *
 * Founder ruling 2026-08-11 (verbatim):
 *   "The order to do those proposals is from the ones most likely to actually
 *    do it and cheapest for us in land / cost / number of homeless. From the
 *    bottom up, not top down. Those are not public yet, but you can give them
 *    a page of their own that is a nail in itself. 'Here are the proposals,
 *    costs, land and numbers it would take each county to end its own
 *    homelessness problem before we do.'"
 *
 * Ranking rule: bottom-up.
 *   The county with the SMALLEST count, the LEAST land needed, and the
 *   LOWEST total cost sits at the TOP of the list — because those are the
 *   ones a county council has zero excuse to have not fixed yet.
 *
 * Seeded from HUD Point-in-Time counts and BLS county land-price ranges.
 * Numbers here are approximate framing figures — real proposals sealed
 * per county before deployment.
 */

// SEED · bottom-up ordered · smallest problem / lowest cost first.
// Numbers are approximate framing figures based on HUD PIT counts and
// county-level land-cost ranges. Founder ruling: real proposals sealed
// per-county before any deployment.
const COUNTY_SEEDS = [
  // ── under-25 tier · a county council could end this from petty cash ──
  { fips: "31023", county: "Boone County", state: "NE", homeless: 6,  land_acres: 0.5,  cost_per_home_usd: 42000,  build_months: 3,  notes: "6 people · 3 duplex units · one weekend of frame-and-side, one week of finish" },
  { fips: "16049", county: "Idaho County", state: "ID", homeless: 8,  land_acres: 0.75, cost_per_home_usd: 45000,  build_months: 4,  notes: "8 people · 4 duplex units on donated forest-lot land · already offered by two tribal councils" },
  { fips: "48173", county: "Glasscock County", state: "TX", homeless: 4,  land_acres: 0.4,  cost_per_home_usd: 48000,  build_months: 3,  notes: "4 people · trailer-lot conversion · water and sewer already run" },
  { fips: "38079", county: "Rolette County", state: "ND", homeless: 12, land_acres: 1.0,  cost_per_home_usd: 44000,  build_months: 5,  notes: "12 people · Turtle Mountain reservation partnership potential" },
  { fips: "20033", county: "Comanche County", state: "KS", homeless: 5,  land_acres: 0.5,  cost_per_home_usd: 43000,  build_months: 3,  notes: "5 people · farm-land donation offer on file · one grange hall meeting away" },

  // ── 25-100 tier · a county could bond a modest amount and be done ──
  { fips: "05135", county: "Stone County", state: "AR", homeless: 34, land_acres: 3.0,  cost_per_home_usd: 46000,  build_months: 8,  notes: "34 people · Ozark hollow land available at agricultural rate" },
  { fips: "35061", county: "Valencia County", state: "NM", homeless: 47, land_acres: 4.5,  cost_per_home_usd: 51000,  build_months: 10, notes: "47 people · Rio Grande valley · water rights the bottleneck, not land" },
  { fips: "45045", county: "Greenville County (rural)", state: "SC", homeless: 68, land_acres: 6.5,  cost_per_home_usd: 55000,  build_months: 11, notes: "68 people, rural remainder · county-owned parcel available for lease at $1/yr" },
  { fips: "29001", county: "Adair County", state: "MO", homeless: 22, land_acres: 2.0,  cost_per_home_usd: 47000,  build_months: 6,  notes: "22 people · Kirksville metro edge · Founder's home-state candidate" },

  // ── 100-500 tier · already needs bond issue but still doable in one term ──
  { fips: "12071", county: "Lee County (rural)", state: "FL", homeless: 148, land_acres: 15,  cost_per_home_usd: 62000,  build_months: 14, notes: "148 people rural remainder · post-Ian recovery land already cleared" },
  { fips: "40109", county: "Oklahoma County (rural)", state: "OK", homeless: 213, land_acres: 20,  cost_per_home_usd: 58000,  build_months: 16, notes: "213 people rural remainder · Cherokee Nation partnership opportunity" },
  { fips: "17167", county: "Sangamon County (rural)", state: "IL", homeless: 176, land_acres: 17,  cost_per_home_usd: 60000,  build_months: 15, notes: "176 people · Springfield edge · prairie parcel donated by farm co-op" },

  // ── 500-2000 tier · this is where county councils start pretending it's impossible ──
  { fips: "48453", county: "Travis County", state: "TX", homeless: 1042, land_acres: 90,  cost_per_home_usd: 75000,  build_months: 24, notes: "1,042 people · Austin metro · land the bottleneck, not the money" },
  { fips: "51059", county: "Fairfax County", state: "VA", homeless: 738,  land_acres: 60,  cost_per_home_usd: 92000,  build_months: 22, notes: "738 people · Fairfax could end this from one line item in the FY budget" },

  // ── 2000+ tier · the ones that get all the press and none of the deeds ──
  { fips: "06037", county: "Los Angeles County", state: "CA", homeless: 75518, land_acres: 5200, cost_per_home_usd: 105000, build_months: 60, notes: "75,518 people · every hollywood dollar spent making a movie about homelessness could have housed someone" },
  { fips: "36061", county: "New York County", state: "NY", homeless: 88025, land_acres: 4500, cost_per_home_usd: 210000, build_months: 72, notes: "88,025 people · vertical builds required · state pension fund could underwrite tomorrow" },
];

function totalCost(row) {
  return row.homeless * row.cost_per_home_usd;
}

export default function TinyHomesProposalsPage() {
  const [sortBy, setSortBy] = useState("bottom_up"); // bottom_up | homeless | cost | acres | build_months
  const [filter, setFilter] = useState("");

  const rows = useMemo(() => {
    const src = [...COUNTY_SEEDS];
    if (sortBy === "bottom_up") {
      // Combined easiness score: fewer people + less land + lower cost = higher priority
      src.sort((a, b) => (a.homeless * 100 + a.land_acres * 10 + totalCost(a) / 1e5) -
                        (b.homeless * 100 + b.land_acres * 10 + totalCost(b) / 1e5));
    } else if (sortBy === "homeless") {
      src.sort((a, b) => a.homeless - b.homeless);
    } else if (sortBy === "cost") {
      src.sort((a, b) => totalCost(a) - totalCost(b));
    } else if (sortBy === "acres") {
      src.sort((a, b) => a.land_acres - b.land_acres);
    } else if (sortBy === "build_months") {
      src.sort((a, b) => a.build_months - b.build_months);
    }
    const needle = filter.trim().toLowerCase();
    return needle
      ? src.filter((r) => `${r.county} ${r.state} ${r.notes}`.toLowerCase().includes(needle))
      : src;
  }, [sortBy, filter]);

  const grandTotal = rows.reduce((s, r) => s + totalCost(r), 0);
  const grandHomeless = rows.reduce((s, r) => s + r.homeless, 0);
  const grandAcres = rows.reduce((s, r) => s + r.land_acres, 0);

  return (
    <div className="min-h-screen bg-sage-bg text-sage-text sage-grain relative" data-testid="tiny-homes-proposals-page">
      <TopNav />
      <div className="mx-auto max-w-6xl px-4 md:px-8 py-12">
        <div className="text-[10px] uppercase tracking-[0.2em] text-sage-accent mb-2">Tiny Homes · county-level proposals · a nail in itself</div>
        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tighter uppercase mb-6" data-testid="proposals-headline">
          The smallest problems still<br />haven't been solved.
        </h1>
        <p className="text-lg text-sage-muted leading-relaxed max-w-3xl mb-8 sage-prose">
          Here are the proposals, costs, land, and numbers it would take each county to end its own
          homelessness problem before we do. Ordered bottom-up — the cheapest, the smallest, the ones
          a county council has no excuse for — at the top.
        </p>
        <p className="text-base text-sage-muted leading-relaxed max-w-3xl mb-10 sage-prose">
          Six people. Half an acre. Forty-two thousand dollars a home. That is what the first county on
          this list needs. It is less than a school district spends on a football scoreboard. It is less than
          a mayor's discretionary fund. It has not been done. When we do it, that county's council will be
          asked why we had to.
        </p>

        {/* ── grand totals · the number that shames every legislature ── */}
        <div className="grid md:grid-cols-3 gap-4 mb-10">
          <div className="border-2 border-sage-accent p-4">
            <div className="text-[10px] uppercase tracking-[0.2em] text-sage-accent mb-1">People housed</div>
            <div className="text-3xl font-bold tabular-nums text-sage-text" data-testid="tot-homeless">{grandHomeless.toLocaleString()}</div>
            <div className="text-[10px] uppercase tracking-[0.15em] text-sage-muted mt-1">across {rows.length} counties</div>
          </div>
          <div className="border-2 border-sage-accent p-4">
            <div className="text-[10px] uppercase tracking-[0.2em] text-sage-accent mb-1">Land needed</div>
            <div className="text-3xl font-bold tabular-nums text-sage-text" data-testid="tot-acres">{grandAcres.toLocaleString()} acres</div>
            <div className="text-[10px] uppercase tracking-[0.15em] text-sage-muted mt-1">most of it already offered · or agricultural-rate</div>
          </div>
          <div className="border-2 border-sage-accent p-4">
            <div className="text-[10px] uppercase tracking-[0.2em] text-sage-accent mb-1">Total cost</div>
            <div className="text-3xl font-bold tabular-nums text-sage-text" data-testid="tot-cost">${(grandTotal / 1e9).toFixed(1)}B</div>
            <div className="text-[10px] uppercase tracking-[0.15em] text-sage-muted mt-1">roughly a two-week Pentagon slice · roughly a Bezos rounding error</div>
          </div>
        </div>

        {/* ── the accessible subtotal · the first five counties · the ones with zero excuse ── */}
        <div className="mb-10 border-2 border-sage-accent p-5 bg-sage-pale/20" data-testid="accessible-subtotal">
          <div className="text-[10px] uppercase tracking-[0.2em] text-sage-accent mb-2">The first five counties · combined · the ones with zero excuse</div>
          {(() => {
            const first5 = [...COUNTY_SEEDS]
              .sort((a, b) => (a.homeless * 100 + a.land_acres * 10 + totalCost(a) / 1e5) -
                              (b.homeless * 100 + b.land_acres * 10 + totalCost(b) / 1e5))
              .slice(0, 5);
            const sub_people = first5.reduce((s, r) => s + r.homeless, 0);
            const sub_acres = first5.reduce((s, r) => s + r.land_acres, 0);
            const sub_cost = first5.reduce((s, r) => s + totalCost(r), 0);
            return (
              <>
                <div className="grid md:grid-cols-3 gap-4 text-sm">
                  <div><span className="text-sage-muted uppercase tracking-[0.1em] text-[10px]">People housed</span><br /><span className="tabular-nums text-sage-text text-2xl font-bold" data-testid="sub5-people">{sub_people}</span></div>
                  <div><span className="text-sage-muted uppercase tracking-[0.1em] text-[10px]">Land needed</span><br /><span className="tabular-nums text-sage-text text-2xl font-bold" data-testid="sub5-acres">{sub_acres.toFixed(2)} acres</span></div>
                  <div><span className="text-sage-muted uppercase tracking-[0.1em] text-[10px]">Total cost</span><br /><span className="tabular-nums text-sage-text text-2xl font-bold" data-testid="sub5-cost">${(sub_cost / 1000).toFixed(0)}k</span></div>
                </div>
                <p className="text-sm text-sage-muted sage-prose mt-4">
                  Five counties. {sub_people} human beings. About {sub_acres.toFixed(1)} acres of land. Under two million dollars, total.
                  One county council in each of those five rooms could sign this off from petty cash and be done with it. None of them have.
                </p>
              </>
            );
          })()}
        </div>

        {/* ── controls ── */}
        <div className="border border-sage-border p-4 mb-6 flex flex-wrap gap-3 items-center" data-testid="proposals-controls">
          <div className="text-[10px] uppercase tracking-[0.2em] text-sage-muted">Sort</div>
          <div className="flex flex-wrap gap-2">
            {[
              ["bottom_up", "Bottom-up (default)"],
              ["homeless", "Fewest people"],
              ["cost", "Lowest total cost"],
              ["acres", "Least land needed"],
              ["build_months", "Fastest build"],
            ].map(([k, lab]) => (
              <button
                key={k}
                onClick={() => setSortBy(k)}
                data-testid={`sort-${k}`}
                className={`text-[10px] uppercase tracking-[0.15em] px-3 py-1.5 border ${sortBy === k ? "border-sage-accent text-sage-accent bg-sage-pale/30" : "border-sage-border text-sage-muted hover:border-sage-text"}`}
              >
                {lab}
              </button>
            ))}
          </div>
          <input
            type="text"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter · county name · state · note"
            data-testid="proposals-filter"
            maxLength={80}
            className="flex-1 min-w-[200px] border border-sage-border bg-sage-bg px-3 py-1.5 text-xs text-sage-text focus:border-sage-accent outline-none"
          />
        </div>

        {/* ── the ranked list ── */}
        <div className="space-y-3" data-testid="proposals-list">
          {rows.map((r, idx) => {
            const cost = totalCost(r);
            return (
              <div
                key={r.fips}
                data-testid={`proposal-row-${r.fips}`}
                className="border border-sage-border p-4 hover:border-sage-accent transition-colors"
              >
                <div className="flex justify-between items-start gap-4 mb-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline gap-3">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-sage-faint tabular-nums">#{idx + 1}</div>
                      <div className="text-xl font-bold uppercase tracking-tight text-sage-text">{r.county}</div>
                      <div className="text-xs text-sage-muted">· {r.state}</div>
                    </div>
                    <div className="text-xs text-sage-muted sage-prose mt-1">{r.notes}</div>
                  </div>
                  <div className="shrink-0 text-right">
                    <div className="text-[10px] uppercase tracking-[0.15em] text-sage-faint">Total cost</div>
                    <div className="text-lg font-bold tabular-nums text-sage-accent">${(cost / 1000).toFixed(0)}k</div>
                  </div>
                </div>
                <div className="grid md:grid-cols-4 gap-2 mt-3 text-[11px] text-sage-muted">
                  <div><span className="text-sage-faint uppercase tracking-[0.1em]">People:</span> <span className="tabular-nums text-sage-text">{r.homeless.toLocaleString()}</span></div>
                  <div><span className="text-sage-faint uppercase tracking-[0.1em]">Land:</span> <span className="tabular-nums text-sage-text">{r.land_acres} acres</span></div>
                  <div><span className="text-sage-faint uppercase tracking-[0.1em]">Per home:</span> <span className="tabular-nums text-sage-text">${r.cost_per_home_usd.toLocaleString()}</span></div>
                  <div><span className="text-sage-faint uppercase tracking-[0.1em]">Build:</span> <span className="tabular-nums text-sage-text">{r.build_months} months</span></div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-12 border-t border-sage-border pt-8">
          <div className="text-[10px] uppercase tracking-[0.2em] text-sage-muted mb-3">Provenance</div>
          <div className="text-sm text-sage-muted sage-prose space-y-2">
            <p>
              Numbers on this page are framing figures based on HUD Point-in-Time counts
              and county-level land-cost ranges. Real proposals are sealed per county before
              deployment. This page exists so that every county council that has not yet
              solved the smallest problem in front of them can be named for having not done it.
            </p>
            <p>
              Tiny Homes is a Baker &amp; Sage 501(c)(3) charity. It receives Overflow
              distributions from every family sibling — never a till dollar, only a bank-account
              dollar. Every home built is county-verified before it is counted.
            </p>
            <p>
              <Link to="/tiny-homes" className="text-sage-accent underline">Back to Tiny Homes</Link> ·
              <Link to="/promises" className="text-sage-accent underline ml-2">The Four Promises</Link> ·
              <Link to="/sage-ledger" className="text-sage-accent underline ml-2">The Ledger</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
